/** localStorage-backed reader prefs (lang, theme, size, family). */

export type Theme = 'system' | 'light' | 'sepia' | 'dark';
export type Size = 's' | 'm' | 'l' | 'xl';
export type Family = 'system' | 'palatino' | 'fern';
export type Lang = 'en' | 'fr';

export type Prefs = {
  lang: Lang;
  theme: Theme;
  size: Size;
  family: Family;
};

const KEY = 'mh:prefs';

function detectLang(): Lang {
  if (typeof navigator === 'undefined') return 'en';
  return navigator.language?.toLowerCase().startsWith('fr') ? 'fr' : 'en';
}

function defaults(): Prefs {
  return { lang: detectLang(), theme: 'system', size: 'm', family: 'system' };
}

export function load(): Prefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return defaults();
    return { ...defaults(), ...JSON.parse(raw) };
  } catch {
    return defaults();
  }
}

export function save(p: Prefs): void {
  try { localStorage.setItem(KEY, JSON.stringify(p)); } catch { /* quota/private mode — silent */ }
}

/** Apply prefs to the root element (mirrors what the inline boot script does). */
export function apply(p: Prefs, root: HTMLElement = document.documentElement): void {
  root.lang = p.lang;
  root.dataset.theme = p.theme;
  root.dataset.size = p.size;
  root.dataset.family = p.family;
}

const LAST_ARTICLE_KEY = 'mh:lastArticle';
export const lastArticle = {
  get(): string | null { try { return localStorage.getItem(LAST_ARTICLE_KEY); } catch { return null; } },
  set(id: string): void { try { localStorage.setItem(LAST_ARTICLE_KEY, id); } catch {} },
};
